import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import type { AdminUserSummary } from "@/features/admin/admin.types";

type AdminTableProps = {
  items: AdminUserSummary[];
  loading?: boolean;
  selectedUserId: string | null;
  onSelectUser: (userId: string) => void;
};

export function AdminTable({
  items,
  loading = false,
  selectedUserId,
  onSelectUser,
}: AdminTableProps) {
  const { i18n, t } = useTranslation();

  if (loading && items.length === 0) {
    return (
      <div className="rounded-[1.6rem] border border-white/10 bg-black/20 px-5 py-10 text-sm text-[#d7cab9]/72">
        {t("adminWorkspace.table.loading")}
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="rounded-[1.6rem] border border-dashed border-white/10 bg-black/20 px-5 py-10 text-sm text-[#d7cab9]/72">
        {t("adminWorkspace.table.empty")}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[1.6rem] border border-white/10 bg-black/20">
      <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_110px_120px_124px] gap-3 border-b border-white/8 px-5 py-4 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#b69d7e]">
        <span>{t("adminWorkspace.table.headers.user")}</span>
        <span>{t("adminWorkspace.table.headers.registered")}</span>
        <span>{t("adminWorkspace.table.headers.tier")}</span>
        <span>{t("adminWorkspace.table.headers.status")}</span>
        <span className="text-right">{t("adminWorkspace.table.headers.credits")}</span>
      </div>

      <div className="divide-y divide-white/6">
        {items.map((item) => {
          const active = item.userId === selectedUserId;
          return (
            <button
              className={`grid w-full grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_110px_120px_124px] gap-3 px-5 py-4 text-left transition ${
                active ? "bg-[#f0d2ae]/12" : "hover:bg-white/[0.045]"
              }`}
              key={item.userId}
              type="button"
              onClick={() => onSelectUser(item.userId)}
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-[#fff6ea]">{item.name || item.email}</p>
                <p className="truncate text-xs text-[#dbcdbd]/65">{item.email}</p>
                {item.testAccount ? (
                  <Badge className="mt-2 w-fit border-[#d2b793]/35 bg-[#d2b793]/16 text-[#f5e8d6]" variant="outline">
                    {t("adminWorkspace.table.testAccount")}
                  </Badge>
                ) : null}
              </div>
              <div className="text-sm text-[#dbcdbd]/75">
                {new Intl.DateTimeFormat(i18n.language, {
                  dateStyle: "medium",
                  timeStyle: "short",
                }).format(new Date(item.registeredAt))}
              </div>
              <div className="text-sm font-semibold uppercase tracking-[0.14em] text-[#efe1cf]">
                {t(`common.tiers.${item.tier}`)}
              </div>
              <div>
                <Badge
                  className={item.status === "active"
                    ? "border-[#b8d5b8]/20 bg-[#b8d5b8]/12 text-[#dbf2db]"
                    : "border-[#f4b1a7]/20 bg-[#f4b1a7]/12 text-[#ffd6d0]"}
                  variant="outline"
                >
                  {t(`adminWorkspace.statuses.${item.status}`)}
                </Badge>
              </div>
              <div className="text-right text-sm font-semibold text-[#fff6ea]">
                {item.creditBalance}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
